import type { UserPlan } from "@/lib/auth/plans";

export type PaidPlan = "pro" | "studio";
export type BillingInterval = "month" | "year";

type PlanPrice = {
  /** What Creem charges, in USD cents — must equal the Creem product's price. */
  cents: number;
  /** As printed in the UI. */
  display: string;
};

/**
 * ЄДИНЕ місце з цінами/квотами/назвами планів. Display-ціни ТУТ мають
 * збігатися з цінами продуктів у Creem (source of truth оплати — Creem;
 * source of truth UI — цей файл). Змінюєш ціну → міняй в обох місцях.
 *
 * Creem products (one per plan × interval; ids live in env, see src/lib/creem.ts):
 *   pro    month CREEM_PRODUCT_ID              year CREEM_PRODUCT_ID_YEARLY
 *   studio month CREEM_PRODUCT_ID_STUDIO       year CREEM_PRODUCT_ID_STUDIO_YEARLY
 * Each is a comma-separated list: checkout sells the first id, and every id in
 * it keeps mapping to its plan. Studio's list starts with the $69.99 / $699
 * products (Studio with API access) and keeps the $34.99 / $349 ones after
 * them, so subscribers from before the price change stay on Studio.
 * All are tax-EXCLUSIVE: VAT/sales tax is added on top at checkout, which is why
 * every price surface says "plus applicable tax".
 */
export const PLAN_CONFIG: Record<
  PaidPlan,
  {
    label: string;
    tagline: string;
    prices: Record<BillingInterval, PlanPrice>;
    /** AI credits per calendar month (UTC). A report costs 1/2/4 by depth. */
    aiCreditsPerMonth: number;
    /** Audience scans per calendar month (UTC). */
    audienceScansPerMonth: number;
    /**
     * Whether someone who doesn't hold the plan can buy it. false = "Coming
     * soon": see isPlanForSale.
     */
    forSale: boolean;
  }
> = {
  pro: {
    label: "Pro",
    tagline: "For devs tracking their game and its comps",
    prices: {
      month: { cents: 1199, display: "$11.99" },
      year: { cents: 11900, display: "$119" },
    },
    aiCreditsPerMonth: 3,
    audienceScansPerMonth: 0,
    forSale: true,
  },
  studio: {
    label: "Studio",
    tagline: "For studios & publishers, with API & MCP access",
    prices: {
      month: { cents: 6999, display: "$69.99" },
      year: { cents: 69900, display: "$699" },
    },
    aiCreditsPerMonth: 25,
    audienceScansPerMonth: 10,
    // Coming soon (Alex, 2026-10-09). Set to true to put Studio on sale.
    forSale: false,
  },
};

/**
 * Whether a plan can be bought by someone who doesn't hold it yet. A plan that
 * isn't for sale is "Coming soon": every button that would buy it is disabled
 * with a "Coming soon" popover and no purchase handler, useBilling's
 * choosePlan refuses it, and the server refuses it too (/api/checkout and
 * /api/billing/change-plan answer 403 coming_soon), so editing the page in dev
 * tools can't sell it. Whoever already holds the plan keeps managing it: a
 * Studio subscriber can still switch to yearly, undo a scheduled downgrade or
 * resume a cancelled subscription.
 */
export function isPlanForSale(plan: PaidPlan): boolean {
  return PLAN_CONFIG[plan].forSale;
}

/** What a refusal says when the plan isn't on sale yet. */
export function comingSoonMessage(plan: PaidPlan): string {
  return `${PLAN_CONFIG[plan].label} is coming soon.`;
}

/** Every paid tier, lowest first. */
export const PAID_PLANS: readonly PaidPlan[] = ["pro", "studio"];

/** The free tier as a card — it has no Creem product. */
export const FREE_PLAN = {
  label: "Free",
  tagline: "For players and the curious",
} as const;

/** Tier order. Studio is a superset of Pro, Pro of Free. */
export const PLAN_RANK: Record<"free" | PaidPlan, number> = {
  free: 0,
  pro: 1,
  studio: 2,
};

/** Rank of any stored/effective plan string; unknown and anonymous read as free. */
export function planRank(plan: string | null | undefined): number {
  return isPaidPlanId(plan) ? PLAN_RANK[plan] : 0;
}

export function isPaidPlanId(v: unknown): v is PaidPlan {
  return v === "pro" || v === "studio";
}

export function isBillingInterval(v: unknown): v is BillingInterval {
  return v === "month" || v === "year";
}

/** "$11.99" / "$119" — cents are dropped only when they are zero. */
export function formatUsd(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars)
    ? `$${dollars.toLocaleString("en-US")}`
    : `$${dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function priceFor(plan: PaidPlan, interval: BillingInterval): PlanPrice {
  return PLAN_CONFIG[plan].prices[interval];
}

/** "/mo" or "/yr". */
export function intervalSuffix(interval: BillingInterval): string {
  return interval === "year" ? "/yr" : "/mo";
}

/** The yearly price spread over 12 months, to the nearest cent ("$9.92"). */
export function monthlyEquivalentCents(plan: PaidPlan): number {
  return Math.round(PLAN_CONFIG[plan].prices.year.cents / 12);
}

/** Whole-percent saving of yearly over 12 monthly payments (both plans: 17). */
export function yearlySavingsPercent(plan: PaidPlan): number {
  const { month, year } = PLAN_CONFIG[plan].prices;
  return Math.round((1 - year.cents / (month.cents * 12)) * 100);
}

/** AI credits a plan gets per calendar month (0 = feature not included). */
export function aiCreditQuotaFor(plan: UserPlan): number {
  return isPaidPlanId(plan) ? PLAN_CONFIG[plan].aiCreditsPerMonth : 0;
}

/** Audience scans a plan gets per calendar month (0 = not included). */
export function audienceScanQuotaFor(plan: UserPlan): number {
  return isPaidPlanId(plan) ? PLAN_CONFIG[plan].audienceScansPerMonth : 0;
}

/**
 * How deep an AI review report reads, and what it costs in credits. Model
 * cost grows with the reviews read: measured on the default model, Standard
 * ≈ $0.72, Deep ≈ $1.40, Max ≈ $2.67 — hence 1 / 2 / 4. Client-safe on
 * purpose (no model names or prices here — those stay server-side).
 */
export const REPORT_DEPTHS = [
  { id: "standard", label: "Standard", maxReviews: 700, credits: 1 },
  { id: "deep", label: "Deep", maxReviews: 1500, credits: 2 },
  { id: "max", label: "Max", maxReviews: 3000, credits: 4 },
] as const;

export type ReportDepth = (typeof REPORT_DEPTHS)[number];
export type ReportDepthId = ReportDepth["id"];

export function isReportDepthId(v: unknown): v is ReportDepthId {
  return REPORT_DEPTHS.some((d) => d.id === v);
}

export function reportDepth(id: ReportDepthId): ReportDepth {
  return REPORT_DEPTHS.find((d) => d.id === id)!;
}

/**
 * Credits for a run that reads `reviews` reviews: the smallest depth that
 * covers it. A 400-review game costs 1 credit even at "Max" — the AI can only
 * read what exists, and the charge follows what it read.
 */
export function creditsForSample(reviews: number): number {
  for (const d of REPORT_DEPTHS) if (reviews <= d.maxReviews) return d.credits;
  return REPORT_DEPTHS[REPORT_DEPTHS.length - 1].credits;
}

/**
 * What a Studio account's API keys may spend (API access is part of Studio),
 * enforced on every /api/v1 and /mcp request (src/features/api-access). One
 * pool per ACCOUNT, shared by all of its keys and the hosted MCP server; the
 * per-minute limit is per KEY.
 *
 * 50,000 units a month (half the 100,000 first proposed, at Alex's call to go
 * tighter): a dashboard refreshing a few hundred games' history every day, or
 * an analyst asking an AI agent questions all day, fits; copying a whole
 * history series for the ~86k games we index would take five months. The daily
 * cap stops a month's allowance from going in one afternoon.
 */
export const API_LIMITS = {
  unitsPerMonth: 50_000,
  unitsPerDay: 5_000,
  requestsPerMinutePerKey: 60,
  maxActiveKeys: 3,
} as const;

/**
 * Units one API call costs, by weight. A lookup or a list page is 1; anything
 * that returns a history series, the regional matrix or a catalog filter is 3;
 * a launch forecast (a K-NN over the training set) is 10. Each endpoint's cost
 * lives in its definition (src/features/api-access/endpoints) as one of these.
 */
export const API_UNIT_COSTS = {
  lookup: 1,
  series: 3,
  forecast: 10,
} as const;

export type ApiCostClass = keyof typeof API_UNIT_COSTS;

export type FeatureValue = boolean | string;

export type PlanFeatureRow = {
  name: string;
  /** Short qualifier shown under the name on wide layouts. */
  hint?: string;
  free: FeatureValue;
  pro: FeatureValue;
  studio: FeatureValue;
};

/**
 * HONEST feature matrix — every paid row is live and gated: history depth,
 * release hours, N&T and the AI quota on the server (planLimits / minPlan /
 * requirePaidAccess / quota); the chart CSV button in the UI only, on purpose
 * (the chart holds nothing the server clamp didn't send); "Priority support"
 * is a service promise, not code. Don't list a feature a subscriber can't
 * find in the UI. "Free" is a signed-in free account; without an account
 * chart history stops at 7 days. Studio's API rows (keys, the hosted MCP
 * server and the units both spend) are gated by hasApiAccess and metered by
 * API_LIMITS.
 */
export const PLAN_FEATURES: PlanFeatureRow[] = [
  { name: "Full catalog, rankings & search", free: true, pro: true, studio: true },
  { name: "Watchlist, compare & More Like This", free: true, pro: true, studio: true },
  { name: "Pre-release hype board", free: true, pro: true, studio: true },
  { name: "Chart history", free: "3 months", pro: "All time", studio: "All time" },
  {
    name: "Release calendar",
    hint: "the exact launch hour is free on every game page; the calendar lists every dated release",
    free: "Next 7 days",
    pro: "Every date · filters · sort",
    studio: "Every date · filters · sort",
  },
  {
    name: "New & Trending",
    hint: "where a game sits on Steam's front-page tab, in 28 countries",
    free: "Summary",
    pro: "Full history",
    studio: "Full history",
  },
  {
    name: "Regional top-sellers matrix",
    hint: "a game's rank in every country, hour by hour (the latest day without an account)",
    free: "Last 7 days",
    pro: "180 days",
    studio: "180 days",
  },
  {
    name: "Launch forecast",
    hint: "expected launch players, built from similar launches",
    free: "Estimate + 2 comps",
    pro: "All 15 comps + best/worst case",
    studio: "All 15 comps + best/worst case",
  },
  { name: "CSV export of any chart", free: false, pro: true, studio: true },
  {
    name: "AI credits",
    hint: "an AI review report costs 1 credit (Standard), 2 (Deep) or 4 (Max)",
    free: false,
    pro: `${PLAN_CONFIG.pro.aiCreditsPerMonth} / month`,
    studio: `${PLAN_CONFIG.studio.aiCreditsPerMonth} / month`,
  },
  {
    name: "Audience insights",
    hint: "what your reviewers own, where they live, which comps they play",
    free: false,
    pro: false,
    studio: `${PLAN_CONFIG.studio.audienceScansPerMonth} scans / month`,
  },
  {
    name: "REST API",
    hint: "versioned JSON endpoints for games, charts, releases and publishers, with an OpenAPI spec",
    free: false,
    pro: false,
    studio: `Up to ${API_LIMITS.maxActiveKeys} keys`,
  },
  {
    name: "Hosted MCP server",
    hint: "plug steampeek into Claude, Cursor and other AI tools",
    free: false,
    pro: false,
    studio: true,
  },
  {
    name: "API units",
    hint: `one pool for the API and the MCP server: a lookup costs ${API_UNIT_COSTS.lookup}, a history series ${API_UNIT_COSTS.series}, a forecast ${API_UNIT_COSTS.forecast}`,
    free: false,
    pro: false,
    studio: `${API_LIMITS.unitsPerMonth.toLocaleString("en-US")} / month`,
  },
  { name: "Priority support", free: false, pro: false, studio: true },
];

/**
 * The few lines a plan card leads with. Each card says what it adds to the
 * tier below ("Everything in Pro, plus"), so the lists stay short.
 */
export const PLAN_HIGHLIGHTS: Record<"free" | PaidPlan, { lead: string | null; items: string[] }> = {
  free: {
    lead: null,
    items: [
      "Every game, chart and ranking",
      "Watchlist, compare & More Like This",
      "Exact release times + 7-day release calendar",
      "3 months of chart history",
    ],
  },
  pro: {
    lead: "Everything in Free, plus",
    items: [
      "All-time chart history",
      "New & Trending history in 28 countries",
      "Full release calendar & 180-day regional matrix",
      "Launch forecast with all 15 similar launches",
      "CSV export of any chart",
      `${PLAN_CONFIG.pro.aiCreditsPerMonth} AI credits a month`,
    ],
  },
  studio: {
    lead: "Everything in Pro, plus",
    items: [
      `${PLAN_CONFIG.studio.aiCreditsPerMonth} AI credits a month`,
      `${PLAN_CONFIG.studio.audienceScansPerMonth} audience scans a month`,
      "API keys, REST API & hosted MCP server",
      `${API_LIMITS.unitsPerMonth.toLocaleString("en-US")} API units a month`,
      "Priority support",
    ],
  },
};

/** Refund promise, shown on the pricing page and in the Terms. */
export const MONEY_BACK_DAYS = 14;
